import {
  CognitoGroupDTO,
  CognitoGroupResponseDTO,
  CreateUniversityDTO,
  PatchUniversityDTO,
  UniversityDTO,
  UniversityIdpDTO,
  UniversityInstanceDTO,
} from "@onetutor/shared/dto";
import { buildQueryString } from "../utils/api.util";
import { onetutor } from "./onetutor";

const university = onetutor.injectEndpoints({
  endpoints: (builder) => ({
    getUniversities: builder.query<UniversityDTO[], void>({
      query: () => "university",
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "university" as const, id })),
              { type: "university", id: "LIST" },
            ]
          : [{ type: "university", id: "LIST" }],
    }),
    getUniversity: builder.query<UniversityDTO, string>({
      query: (universityId) => `university/${universityId}`,
      providesTags: (_result, _error, universityId) => [
        { type: "university", id: universityId },
      ],
    }),
    createUniversity: builder.mutation<UniversityDTO, CreateUniversityDTO>({
      query: (university) => ({
        url: "university",
        method: "POST",
        body: university,
      }),
      invalidatesTags: [
        { type: "university", id: "LIST" },
        { type: "university", id: "INSTANCES" },
      ],
    }),
    updateUniversity: builder.mutation<
      UniversityDTO,
      { universityId: string; university: PatchUniversityDTO }
    >({
      query: ({ universityId, university }) => ({
        url: `university/${universityId}`,
        method: "PATCH",
        body: university,
      }),
      invalidatesTags: (_result, _error, { universityId }) => [
        { type: "university", id: universityId },
        { type: "university", id: "LIST" },
        { type: "university", id: "INSTANCES" },
      ],
    }),
    deleteUniversity: builder.mutation<void, string>({
      query: (universityId) => ({
        url: `university/${universityId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, universityId) => [
        { type: "university", id: universityId },
        { type: "university", id: "LIST" },
        { type: "university", id: "INSTANCES" },
      ],
    }),
    getCognitoGroupsByUniversityId: builder.query<
      CognitoGroupResponseDTO[],
      string
    >({
      query: (universityId) => `auth/cognito/university/${universityId}/groups`,
      providesTags: (_result, _error, universityId) => [
        { type: "cognitoGroups", id: universityId },
      ],
    }),
    addCognitoGroup: builder.mutation<
      void,
      { universityId: string; cognitoGroup: CognitoGroupDTO }
    >({
      query: ({ universityId, cognitoGroup }) => ({
        url: `auth/cognito/university/${universityId}/group`,
        method: "POST",
        body: cognitoGroup,
      }),
      invalidatesTags: (_result, _error, { universityId }) => [
        { type: "cognitoGroups", id: universityId },
      ],
    }),
    deleteCognitoGroup: builder.mutation<
      void,
      { universityId: string; groupId: string }
    >({
      query: ({ universityId, groupId }) => ({
        url: `auth/cognito/university/${universityId}/group/${groupId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { universityId }) => [
        { type: "cognitoGroups", id: universityId },
      ],
    }),
    getUniversityIdp: builder.query<UniversityIdpDTO, void>({
      query: () =>
        buildQueryString("university/idp", {
          origin: window.location.origin,
        }),
      providesTags: [{ type: "university", id: "IDP" }],
    }),
    getUniversityInstances: builder.query<UniversityInstanceDTO[], void>({
      query: () => "university/instances",
      providesTags: [{ type: "university", id: "INSTANCES" }],
    }),
  }),
});

export const {
  useGetUniversitiesQuery,
  useCreateUniversityMutation,
  useUpdateUniversityMutation,
  useDeleteUniversityMutation,
  useGetCognitoGroupsByUniversityIdQuery,
  useAddCognitoGroupMutation,
  useDeleteCognitoGroupMutation,
  useGetUniversityIdpQuery,
  useGetUniversityInstancesQuery,
  useLazyGetUniversityQuery,
} = university;
